import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@store/hooks';
import { selectSpecialAreaCards, type SpecialAreaCard } from '@store/selectors/monitor.selectors';
import { tradeLabel } from '@utils/tradeLabel';
import { LayerChipRow } from '../layers/LayerChips';
import { useLayerChips, type ILayerChips } from '../layers/useLayerData';
import { useReplayViewContext } from '../replay/ReplayViewContext';
import { replaySpecialAreaCards } from '../replay/replaySpecialAreas';
import classes from './mainView.module.css';

interface ISpecialAreaRowProps {
  card: SpecialAreaCard;
  /** The active Activities / Issues layer's chips; null in the Workforce layer. */
  layerChips: ILayerChips | null;
}

export function SpecialAreas() {
  const { t } = useTranslation();
  const liveCards = useAppSelector(selectSpecialAreaCards);
  const replay = useReplayViewContext();
  const cards = useMemo(() => (replay ? replaySpecialAreaCards(liveCards, replay) : liveCards), [liveCards, replay]);
  // In the Activities / Issues layer each area shows that layer's chips, like the building's floors.
  const layerChips = useLayerChips();
  if (cards.length === 0) return null;
  // Trusstor's main view sorts special floors by floorNick.
  const sorted = [...cards].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <section className={classes.specialAreas} aria-label="Special areas">
      <h2 className={classes.specialAreasTitle}>{t('monitor.specialAreas.title')}</h2>

      <header className={classes.specialAreasHead}>
        <span>{t('monitor.specialAreas.colArea')}</span>
        <span>{t('monitor.specialAreas.colResources')}</span>
        <span className={classes.specialAreasHeadInfo}>{t('monitor.specialAreas.colInfo')}</span>
      </header>

      {sorted.map((card) => (
        <SpecialAreaRow key={card.floorId} card={card} layerChips={layerChips} />
      ))}
    </section>
  );
}

function SpecialAreaRow({ card, layerChips }: ISpecialAreaRowProps) {
  const { t } = useTranslation();
  const isEmpty =
    card.workersCount === 0
    && card.equipmentCount === 0
    && !card.hasOperator
    && card.professionChips.length === 0;

  return (
    <article className={classes.specialAreaRow}>
      <div className={classes.areaTitle}>
        <div className={classes.areaTitleRow}>
          {card.isMoving ? (
            <span className={classes.areaIcon} aria-hidden="true">
              <ElevatorGlyph />
            </span>
          ) : null}
          <span className={classes.areaName}>{card.name}</span>
        </div>
        {card.isMoving && card.currentFloorLabel ? (
          <div className={classes.areaMeta}>
            <span className={classes.areaMetaItem}>
              <PinGlyph />
              {card.currentFloorLabel}
            </span>
            <span className={classes.areaMetaItem}>
              <PulseGlyph />
              {t('monitor.specialAreas.active')}
            </span>
          </div>
        ) : null}
      </div>

      <div className={classes.resourceRow}>
        {layerChips ? (
          <LayerChipRow
            floorId={card.floorId}
            chips={layerChips.byFloor.get(card.floorId) ?? []}
            layer={layerChips.layer}
            floorOfArea={layerChips.floorOfArea}
          />
        ) : isEmpty ? (
          <span className={classes.areaEmpty}>{t('monitor.specialAreas.empty')}</span>
        ) : (
          <>
            {card.professionChips.map((chip) => {
              const label = tradeLabel(t, chip.tradeId);
              return (
                <span
                  key={chip.professionId}
                  className={classes.profChip}
                  style={{ background: chip.color }}
                >
                  <span className={classes.profChipCount}>{chip.count}</span>
                  <span className={classes.profChipLabel}>{label}</span>
                </span>
              );
            })}
            {card.operatorName ? (
              <span className={classes.managerChip}>
                <span className={classes.managerChipInitials}>
                  {initials(card.operatorName)}
                </span>
                {card.operatorName}
              </span>
            ) : null}
            {card.equipmentCount > 0 ? (
              <span className={classes.equipChip} title={`${card.equipmentCount} equipment`}>
                <span className={classes.equipChipCount}>{card.equipmentCount}</span>
                <DrillGlyph />
              </span>
            ) : null}
          </>
        )}
      </div>

      <div className={classes.info}>
        {card.alertsCount > 0 ? (
          <span className={classes.infoIcon} aria-label={`${card.alertsCount} safety alerts`}>
            <AlertGlyph />
            <span className={classes.infoBadge}>{card.alertsCount}</span>
          </span>
        ) : card.hasOperator && !card.operatorName ? (
          <span className={classes.infoOperator} aria-label="Operator absent">
            <OperatorAbsentGlyph />
            {t('monitor.specialAreas.operator')}
          </span>
        ) : null}
      </div>
    </article>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]!.toUpperCase())
    .join('');
}

function ElevatorGlyph() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="3" width="16" height="18" rx="1.5" />
      <path d="M9 10l3-3 3 3" />
      <path d="M9 14l3 3 3-3" />
    </svg>
  );
}

function PinGlyph() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 22s7-7.5 7-13a7 7 0 1 0-14 0c0 5.5 7 13 7 13z" />
      <circle cx="12" cy="9" r="2.5" />
    </svg>
  );
}

function PulseGlyph() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function DrillGlyph() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 12h7l2-3h5a3 3 0 0 1 3 3v0a3 3 0 0 1-3 3h-5l-2-3H3z" />
      <path d="M3 9v6" />
    </svg>
  );
}

function AlertGlyph() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3 2 21h20L12 3z" />
      <path d="M12 10v5" />
      <circle cx="12" cy="18" r="0.6" fill="currentColor" />
    </svg>
  );
}

function OperatorAbsentGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--red)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="9" cy="8" r="3.5" />
      <path d="M3 20c1-3.5 3.4-5 6-5s5 1.5 6 5" />
      <line x1="16" y1="6" x2="22" y2="12" />
      <line x1="22" y1="6" x2="16" y2="12" />
    </svg>
  );
}
